import Styles from './Tabs.module.scss'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { Tab } from './Tab'
import { UserRole } from 'types'
import { useWorkspace } from 'hooks/useWorkspace'
import { useAbility } from '@casl/react'
import { AbilityContext } from 'components/Can'
import { SubjectType } from 'utils/caslAbility'
import { TabsProps } from './Types'
import { AddElements, AddFont1, Brand, Grid, ImageAdd, Caption, Download, Send1, Bg, ImageResize } from 'blaze-ui/icons'
import { PropsWithChildren } from 'react'
import { channelSupportsCaption } from '../utils/channelSupportsCaption'
import { DesignChannel } from 'components/NewFileModal/Types'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'
import { useBlankFrameHints } from '../utils/useBlankFrameHints'
import { getCurrentFrame } from 'components/VisualStudioPlayground/VisualEditor/helpers'
import { FrameShape } from 'components/VisualStudioPlayground/Shapes/FrameShape/Types'
import { FrameShapeUtil } from 'components/VisualStudioPlayground/Shapes/FrameShape/FrameShapeUtil'
import { track } from '@tldraw/tldraw'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useIsScheduledPostsV3 } from 'hooks/isScheduledPostsV3'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

const TabGroup = (props: PropsWithChildren) => <div className={Styles.tabGroup} {...props} />

const Tabs = track(
  observer(({ file }: TabsProps) => {
    const isDesignerResizeEnabled = useFlipperFlag('designer_resize')
    const isSystemRedesignEnabled = useIsSystemRedesign()
    const isScheduledPostsV3 = useIsScheduledPostsV3()
    const { designStore } = useRootStore()
    const workspace = useWorkspace(file.workspace.id)
    const ability = useAbility(AbilityContext)
    const { isChannelSocialPostable } = useIsChannelPostable()
    const authedFile = {
      role: workspace?.role || UserRole.UNAUTHED,
      version_control_mode: file.workspace.version_control_mode,
      permissions: file?.permissions,
      __caslSubjectType__: SubjectType.DESIGN,
    }

    const can: typeof ability.can = (action, subject) => {
      return ability.can(action, subject)
    }

    if (!designStore) return null

    const handlePublishTabPress = async () => {
      designStore.openTab('publish')
    }

    const activeTab = designStore.tab
    const activePanel = designStore.panel
    const hasActiveTab = activeTab !== undefined
    const hasActivePanel = activePanel !== undefined
    const highlightActiveTab = hasActiveTab && !hasActivePanel
    const supportsCaption = channelSupportsCaption(file.channel as DesignChannel)
    const isPostable = isChannelSocialPostable(file.channel as DesignChannel)
    const showBlankFrameHints = useBlankFrameHints()
    const currentFrame = getCurrentFrame(designStore.editor)
    const isCurrentFrameBlank =
      currentFrame &&
      (designStore.editor.getShapeUtil<FrameShape>(currentFrame) as FrameShapeUtil).isBlank(currentFrame)
    const isMultiPageDesign =
      designStore.editor.getCurrentPageShapes().filter((shape) => shape.type === 'frame').length > 1

    const publishTabLabel = isScheduledPostsV3 ? 'Post' : 'Publish'
    return (
      <>
        <TabGroup>
          <Tab
            label="Elements"
            icon={() => <AddElements size={isSystemRedesignEnabled ? 18 : 20} />}
            isActive={highlightActiveTab && activeTab === 'elements'}
            onPress={() => designStore.openTab('elements')}
            tourId="design-tour-1"
          />
          {isSystemRedesignEnabled && <div className={Styles.separator} />}
          <Tab
            label="Media"
            icon={() => <ImageAdd size={isSystemRedesignEnabled ? 18 : 20} />}
            isActive={highlightActiveTab && activeTab === 'uploads'}
            onPress={() => designStore.openTab('uploads')}
            tourId="design-tour-2"
          />
          {isSystemRedesignEnabled && <div className={Styles.separator} />}
          <Tab
            label="Text"
            icon={() => <AddFont1 size={isSystemRedesignEnabled ? 18 : 20} />}
            isActive={highlightActiveTab && activeTab === 'text'}
            onPress={() => designStore.openTab('text')}
            tourId="design-tour-3"
          />
        </TabGroup>

        <TabGroup>
          <Tab
            label="Templates"
            icon={() => <Grid size={isSystemRedesignEnabled ? 18 : 20} />}
            isActive={highlightActiveTab && activeTab === 'templates'}
            onPress={() => designStore.openTab('templates')}
            tourId="design-tour-4"
          />
          {isSystemRedesignEnabled && <div className={Styles.separator} />}
          <Tab
            label="Backgrounds"
            icon={() => <Bg size={isSystemRedesignEnabled ? 18 : 20} />}
            isActive={highlightActiveTab && activeTab === 'backgrounds'}
            onPress={() => designStore.openTab('backgrounds')}
            withDot={showBlankFrameHints && isCurrentFrameBlank}
            tourId="design-tour-5"
          />
          {isDesignerResizeEnabled && !isMultiPageDesign && (
            <>
              {isSystemRedesignEnabled && <div className={Styles.separator} />}
              <Tab
                label="Resize"
                icon={() => <ImageResize size={isSystemRedesignEnabled ? 18 : 20} />}
                isActive={highlightActiveTab && activeTab === 'resize'}
                onPress={() => designStore.openTab('resize')}
              />
            </>
          )}
        </TabGroup>

        <TabGroup>
          {can('edit', authedFile) && (
            <Tab
              label="Brand"
              icon={() => <Brand size={isSystemRedesignEnabled ? 18 : 20} />}
              isActive={highlightActiveTab && activeTab === 'brand'}
              onPress={() => designStore.openTab('brand')}
              tourId="design-tour-6"
            />
          )}
          {supportsCaption && !isScheduledPostsV3 && (
            <>
              {isSystemRedesignEnabled && <div className={Styles.separator} />}
              <Tab
                label="Captions"
                icon={() => <Caption size={isSystemRedesignEnabled ? 18 : 20} />}
                isActive={highlightActiveTab && activeTab === 'caption'}
                onPress={() => designStore.openTab('caption')}
              />
            </>
          )}
          {isPostable && (
            <>
              {isSystemRedesignEnabled && <div className={Styles.separator} />}
              <Tab
                label={isPostable ? publishTabLabel : 'Download'}
                icon={() =>
                  isPostable ? (
                    <Send1 size={isSystemRedesignEnabled ? 18 : 20} />
                  ) : (
                    <Download size={isSystemRedesignEnabled ? 18 : 20} />
                  )
                }
                isActive={highlightActiveTab && activeTab === 'publish'}
                onPress={handlePublishTabPress}
                tourId="design-tour-7"
              />
            </>
          )}
        </TabGroup>
      </>
    )
  }),
)

export { Tabs }
